import firebase from 'firebase/app'

import * as auth from './auth'
import * as apiTypes from '../../../common/api'
import * as types from './types'

import { SiteConfig } from './types'
/* eslint-disable @typescript-eslint/no-var-requires */
const { siteMetadata }: SiteConfig =
  require('../../gatsby-config.ts') as SiteConfig
/* eslint-enable @typescript-eslint/no-var-requires */

const getServerUri = () => siteMetadata.config.serverUri

export type RequestOptions = {
  method: string
  endpoint: string
  bearerToken: string
  body?: string
}

async function makeRequestToServer(options: RequestOptions): Promise<Response> {
  const config: RequestInit = {
    method: options.method,
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${options.bearerToken}`,
    },
    redirect: 'follow',
    referrerPolicy: 'no-referrer',
  }
  if (options.body) {
    config.body = options.body
  }
  const response = await fetch(options.endpoint, config)

  return response
}

export async function getAccountId(
  firebaseApp: firebase.FirebaseApp,
): Promise<types.Option<string>> {
  const bearerToken = await auth.mintBearerToken(firebaseApp)
  const response = await makeRequestToServer({
    endpoint: getServerUri() + 'f/v1/accounts',
    method: 'GET',
    bearerToken,
  })

  if (!response.ok) {
    let errorMessage = 'Unknown error'
    try {
      const body = (await response.json()) as apiTypes.CommonResponseBody
      errorMessage = body.msg
    } catch (error) {
      const msg = types.isError(error) ? error.message : JSON.stringify(error)
      throw new Error(`Account response failed to parse: ${msg}`)
    }

    throw new Error(`Account retrieval failed: ${errorMessage}`)
  }

  try {
    const { data: accounts } =
      (await response.json()) as apiTypes.GetAccountResponseBody

    if (accounts.length < 1) {
      return types.None
    }

    const accountId = accounts[0].id

    if (!accountId) {
      throw new Error('No accounts were found')
    }

    return accountId
  } catch (error) {
    const msg = types.isError(error) ? error.message : JSON.stringify(error)
    throw new Error(`Account response failed to parse: ${msg}`)
  }
}

export async function completeDropboxAuthorizationFlow(
  firebaseApp: firebase.FirebaseApp,
  oauthRedirectCode: string,
): Promise<string> {
  const bearerToken = await auth.mintBearerToken(firebaseApp)
  const response = await makeRequestToServer({
    endpoint: getServerUri() + 'f/v1/dropbox/authorizations/complete',
    method: 'POST',
    bearerToken,
    body: JSON.stringify({
      oauthRedirectCode,
    }),
  })

  if (!response.ok) {
    let errorMessage = 'Unknown error'
    try {
      const body = (await response.json()) as apiTypes.CommonResponseBody
      errorMessage = body.msg
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : JSON.stringify(error)
      throw new Error(
        `Failed to get a valid Dropbox authorization URL: ` + `${errorMessage}`,
      )
    }
    throw new Error(
      `Dropbox authorization URL retrieval failed: ${errorMessage}`,
    )
  }

  try {
    const {
      data: { sourceId },
    } = (await response.json()) as apiTypes.CompleteDropboxAuthorizationFlowResponseBody

    return sourceId
  } catch (error) {
    const msg = types.isError(error) ? error.message : JSON.stringify(error)
    throw new Error(`Source ID response failed to parse: ${msg}`)
  }
}

export async function createSource(
  firebaseApp: firebase.FirebaseApp,
  projectId: string,
  source: types.Source,
): Promise<string> {
  // TODO(alex): Validate Source

  const bearerToken = await auth.mintBearerToken(firebaseApp)
  const response = await makeRequestToServer({
    endpoint: getServerUri() + `f/v1/projects/${projectId}/sources/`,
    method: 'POST',
    bearerToken,
    body: JSON.stringify(source),
  })

  if (!response.ok) {
    let errorMessage = 'Unknown error'
    try {
      const body = (await response.json()) as apiTypes.CommonResponseBody
      errorMessage = body.msg
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : JSON.stringify(error)
      throw new Error(
        `Failed to create source for ${projectId}: ${errorMessage}`,
      )
    }
    throw new Error(`Failed to create source for ${projectId}: ${errorMessage}`)
  }

  try {
    const {
      data: { id: sourceId },
    } = (await response.json()) as apiTypes.CreateSourceResponseBody

    return sourceId
  } catch (error) {
    const msg = types.isError(error) ? error.message : JSON.stringify(error)
    throw new Error(`Source ID response failed to parse: ${msg}`)
  }
}

export async function getSource(
  firebaseApp: firebase.FirebaseApp,
  projectId: string,
  sourceId: string,
): Promise<Partial<types.Source>> {
  const bearerToken = await auth.mintBearerToken(firebaseApp)
  const response = await makeRequestToServer({
    endpoint: getServerUri() + `f/v1/projects/${projectId}/sources/${sourceId}`,
    method: 'GET',
    bearerToken,
  })

  if (!response.ok) {
    let errorMessage = 'Unknown error'
    try {
      const body = (await response.json()) as apiTypes.CommonResponseBody
      errorMessage = body.msg
    } catch (error) {
      const msg = types.isError(error) ? error.message : JSON.stringify(error)
      throw new Error(`Source response failed to parse: ${msg}`)
    }
    throw new Error(`Getting source failed: ${errorMessage}`)
  }

  try {
    const {
      data: { value: source },
    } = (await response.json()) as apiTypes.GetSourceResponseBody

    return source
  } catch (error) {
    const msg = types.isError(error) ? error.message : JSON.stringify(error)
    throw new Error(`Source response failed to parse: ${msg}`)
  }
}

export async function setSourceAsPrimary(
  firebaseApp: firebase.FirebaseApp,
  projectId: string,
  newPrimarySourceId: string,
): Promise<void> {
  const bearerToken = await auth.mintBearerToken(firebaseApp)
  const response = await makeRequestToServer({
    endpoint: getServerUri() + `f/v1/projects/${projectId}/sources/primary`,
    method: 'POST',
    bearerToken,
    body: JSON.stringify({
      id: newPrimarySourceId,
    }),
  })

  if (!response.ok) {
    let errorMessage = 'Unknown error'
    try {
      const body = (await response.json()) as apiTypes.CommonResponseBody
      errorMessage = body.msg
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : JSON.stringify(error)
      throw new Error(
        `Failed to update primary_source to ${newPrimarySourceId} ` +
          `on project ${projectId}: ` +
          `${errorMessage}`,
      )
    }
    throw new Error(
      `Failed to update primary_source to ${newPrimarySourceId} ` +
        `on project ${projectId}: ` +
        `${errorMessage}`,
    )
  }
}

export async function mintDropboxAuthorizationUrl(
  firebaseApp: firebase.FirebaseApp,
  projectId: string,
): Promise<string> {
  const bearerToken = await auth.mintBearerToken(firebaseApp)
  const response = await makeRequestToServer({
    endpoint: getServerUri() + `f/v1/dropbox/authorizations/${projectId}`,
    method: 'POST',
    bearerToken,
  })

  if (!response.ok) {
    let errorMessage = 'Unknown error'
    try {
      const body = (await response.json()) as apiTypes.CommonResponseBody
      errorMessage = body.msg
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : JSON.stringify(error)
      throw new Error(
        `Failed to get a valid Dropbox authorization URL: ` + `${errorMessage}`,
      )
    }
    throw new Error(
      `Dropbox authorization URL retrieval failed: ${errorMessage}`,
    )
  }

  try {
    const {
      data: { authUrl },
    } = (await response.json()) as apiTypes.MintDropboxAuthorizationUrlResponseBody

    return authUrl
  } catch (error) {
    const msg = types.isError(error) ? error.message : JSON.stringify(error)
    throw new Error(
      `Dropbox authorization URL response failed to parse: ${msg}`,
    )
  }
}

export async function updateSubscriptionPaymentMethod(
  firebaseApp: firebase.FirebaseApp,
  accountId: string,
  paymentMethodId: string,
): Promise<void> {
  const bearerToken = await auth.mintBearerToken(firebaseApp)
  const response = await makeRequestToServer({
    endpoint: getServerUri() + 'f/v1/subscriptions/' + accountId,
    method: 'PUT',
    bearerToken,
    body: JSON.stringify({
      paymentMethodId,
    }),
  })

  if (!response.ok) {
    let errorMessage = 'Unknown error'
    try {
      const body = (await response.json()) as apiTypes.CommonResponseBody
      errorMessage = body.msg
    } catch (error) {
      const msg = types.isError(error) ? error.message : JSON.stringify(error)
      throw new Error(`Subscription update response failed to parse: ${msg}`)
    }

    throw new Error(`Subscription payment update failed: ${errorMessage}`)
  }

  return
}

export async function cancelSubscription(
  firebaseApp: firebase.FirebaseApp,
  accountId: string,
): Promise<void> {
  const bearerToken = await auth.mintBearerToken(firebaseApp)
  const response = await makeRequestToServer({
    endpoint: getServerUri() + 'f/v1/subscriptions/' + accountId,
    method: 'PUT',
    bearerToken,
    body: JSON.stringify({
      status: types.SubscriptionStatus.Inactive,
    }),
  })

  if (!response.ok) {
    let errorMessage = 'Unknown error'
    try {
      const body = (await response.json()) as apiTypes.CommonResponseBody
      errorMessage = body.msg
    } catch (error) {
      const msg = types.isError(error) ? error.message : JSON.stringify(error)
      throw new Error(
        `Subscription cancellation response failed to parse: ${msg}`,
      )
    }
    throw new Error(`Subscription cancellation failed: ${errorMessage}`)
  }

  return
}

export async function getSubscriptionStatus(
  firebaseApp: firebase.FirebaseApp,
  accountId: string,
): Promise<types.SubscriptionStatus> {
  const bearerToken = await auth.mintBearerToken(firebaseApp)
  const response = await makeRequestToServer({
    endpoint: `${getServerUri()}f/v1/subscriptions/${accountId}/status`,
    method: 'GET',
    bearerToken,
  })

  if (!response.ok) {
    let errorMessage = 'Unknown error'
    try {
      const body =
        (await response.json()) as apiTypes.GetSubscriptionStatusResponseBody
      errorMessage = body.msg
    } catch (error) {
      const msg = types.isError(error) ? error.message : JSON.stringify(error)
      throw new Error(`Subscription status response failed to parse: ${msg}`)
    }
    throw new Error(`Subscription status retrieval failed: ${errorMessage}`)
  }

  try {
    const {
      data: { status },
    } = (await response.json()) as apiTypes.GetSubscriptionStatusResponseBody

    if (!Object.values(types.SubscriptionStatus).includes(status)) {
      return types.SubscriptionStatus.Unknown
    }

    return status
  } catch (error) {
    const msg = types.isError(error) ? error.message : JSON.stringify(error)
    throw new Error(`Subscription status response failed to parse: ${msg}`)
  }
}

export async function addDeployKeyToSource(
  firebaseApp: firebase.FirebaseApp,
  projectId: string,
  sourceId: string,
): Promise<string> {
  const bearerToken = await auth.mintBearerToken(firebaseApp)
  const response = await makeRequestToServer({
    endpoint: `${getServerUri()}f/v1/projects/${projectId}/sources/${sourceId}/ssh-keys`,
    method: 'POST',
    bearerToken,
    body: JSON.stringify({
      projectId,
      sourceId,
    }),
  })

  const body = await response?.text()
  // TODO(alex): Communicate errors to user
  if (response.status !== 201 || !body) {
    throw new Error(
      `Add deploy key request failed with status ${response.status}`,
    )
  }

  try {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const deployCredential = JSON.parse(body)?.data?.source
      ?.deployCredential as string | undefined
    if (!deployCredential || typeof deployCredential !== 'string') {
      throw new Error(
        'Generating deploy key returned response that had no credential ID',
      )
    }

    return deployCredential
  } catch (error) {
    throw new Error(
      'Generating deploy key returned response that was not parseable',
    )
  }
}

// TODO(alex): Migrate to backend API instead of using Firestore directly.
export async function getProjectPrimarySource(
  firebaseApp: firebase.FirebaseApp,
  projectId: string,
): Promise<types.FirestoreStored<types.Source>> {
  // TODO(alex): Switch to using getProject here instead of replicating the same
  // functionality.
  const project = await getProject(firebaseApp, projectId)
  if (!project.primary_source) {
    throw new Error(
      `No primary source field found on project: ${JSON.stringify(project)}`,
    )
  }

  const sourceId = project.primary_source.split('/')[1]
  const sourceView = await getSource(firebaseApp, projectId, sourceId)

  const primarySource = parseSourceViewIntoFirestoreStored(sourceId, sourceView)
  if (types.isError(primarySource)) {
    const error = primarySource
    throw error
  }

  return primarySource
}

export async function getProjectIdsForAccount(
  firebaseApp: firebase.FirebaseApp,
  accountId: string,
): Promise<string[]> {
  const bearerToken = await auth.mintBearerToken(firebaseApp)
  const response = await makeRequestToServer({
    endpoint: getServerUri() + 'f/v1/accounts',
    method: 'GET',
    bearerToken,
  })

  if (!response.ok) {
    let errorMessage = 'Unknown error'
    try {
      const body = (await response.json()) as apiTypes.CommonResponseBody
      errorMessage = body.msg
    } catch (error) {
      const msg = types.isError(error) ? error.message : JSON.stringify(error)
      throw new Error(`Account response failed to parse: ${msg}`)
    }

    throw new Error(`Account retrieval failed: ${errorMessage}`)
  }

  try {
    const { data: accounts } =
      (await response.json()) as apiTypes.GetAccountResponseBody
    if (!accounts) {
      throw new Error('Data field was not found')
    }

    if (accounts.length < 1) {
      throw new Error('No accounts were found')
    }

    if (accounts[0].id !== accountId) {
      throw new Error(`Requested account ${accountId} was not found.`)
    }

    const projects = accounts[0].value?.projects

    if (!projects || Object.keys(projects).length === 0) {
      throw new Error('No projects were found')
    }

    return Object.keys(projects)
  } catch (error) {
    const msg = types.isError(error) ? error.message : JSON.stringify(error)
    throw new Error(`Account response failed to parse: ${msg}`)
  }
}

export async function getProject(
  firebaseApp: firebase.FirebaseApp,
  projectId: string,
): Promise<Partial<types.Project>> {
  const bearerToken = await auth.mintBearerToken(firebaseApp)
  const response = await makeRequestToServer({
    endpoint: getServerUri() + `f/v1/projects/${projectId}`,
    method: 'GET',
    bearerToken,
  })

  const body = await response?.text()
  // TODO(alex): Communicate errors to user
  if (!response.ok || !body) {
    throw new Error(`Get project request failed with status ${response.status}`)
  }

  try {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const partialProject = JSON.parse(body)?.data?.project as
      | Partial<types.Project>
      | undefined
    if (!partialProject) {
      throw new Error('')
    }

    const result: Partial<types.Project> = {}
    if (partialProject.primary_source) {
      result.primary_source = partialProject.primary_source
    }

    if (partialProject.domain_aliases) {
      result.domain_aliases = partialProject.domain_aliases
    }

    if (partialProject.pinned_domain_alias) {
      result.pinned_domain_alias = partialProject.pinned_domain_alias
    }

    return result
  } catch (error) {
    const errorMessage = types.isError(error)
      ? error.message
      : JSON.stringify(error)
    throw new Error(
      `Getting project response was not parseable: ${errorMessage}`,
    )
  }
}

export async function proposeNewDomainAlias(
  firebaseApp: firebase.FirebaseApp,
  projectId: string,
  domain: string,
): Promise<types.FirestoreStored<types.DomainAlias>> {
  // TODO(alex): Validate proposed domain alias
  const bearerToken = await auth.mintBearerToken(firebaseApp)
  const response = await makeRequestToServer({
    endpoint:
      getServerUri() + `f/v1/projects/${projectId}/domains/${domain}/proposals`,
    method: 'POST',
    bearerToken,
  })

  const body = await response?.text()
  // TODO(alex): Communicate errors to user
  if (![200, 201].includes(response.status) || !body) {
    throw new Error(
      `Proposing domain alias failed with status ${response.status}`,
    )
  }

  // TODO(alex): Extract this validation into a helper
  let receivedFsDomainAlias
  try {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    receivedFsDomainAlias = JSON.parse(body)?.data?.current as
      | types.FirestoreStored<types.DomainAlias>
      | undefined
    if (receivedFsDomainAlias === undefined) {
      throw new Error('Domain alias proposal response had no "current" value.')
    }

    if (
      typeof receivedFsDomainAlias.id !== 'string' ||
      receivedFsDomainAlias.id === ''
    ) {
      throw new Error('Received domain alias proposal had invalid "id" field')
    }

    if (
      typeof receivedFsDomainAlias.path !== 'string' ||
      receivedFsDomainAlias.path === '' ||
      receivedFsDomainAlias.path.split('/').length !== 4
    ) {
      throw new Error('Received domain alias proposal had invalid "path" field')
    }

    if (typeof receivedFsDomainAlias.value.domain !== 'string') {
      throw new Error(
        'Received domain alias proposal had invalid "value.domain" field',
      )
    }

    if (
      typeof receivedFsDomainAlias.value.project !== 'string' ||
      receivedFsDomainAlias.value.project === ''
    ) {
      throw new Error(
        'Received domain alias proposal had invalid "value.project" field',
      )
    }

    if (
      typeof receivedFsDomainAlias.value.status !== 'string' ||
      !Object.values(types.DomainAssociationStatus).includes(
        receivedFsDomainAlias.value.status,
      )
    ) {
      throw new Error(
        'Received domain alias proposal had invalid "value.status" field',
      )
    }

    // TODO(alex): validate last_status_update_time
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : JSON.stringify(error)
    throw new Error(
      `Domain alias proposal response failed to parse: ${errorMessage}`,
    )
  }

  return receivedFsDomainAlias
}

export async function getCurrentAliasStatus(
  firebaseApp: firebase.FirebaseApp,
  domain: string,
  projectId: string,
): Promise<types.DomainAssociationStatus> {
  const bearerToken = await auth.mintBearerToken(firebaseApp)
  const response = await makeRequestToServer({
    endpoint:
      getServerUri() + `f/v1/projects/${projectId}/domains/${domain}/status`,
    method: 'GET',
    bearerToken,
  })

  const body = await response?.text()
  // TODO(alex): Communicate errors to user
  if (response.status !== 200 || !body) {
    throw new Error(
      `Getting domain alias status failed with response code ${response.status}`,
    )
  }

  let receivedDomainAliasStatus
  try {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    receivedDomainAliasStatus = JSON.parse(body)?.data?.alias?.status as
      | types.DomainAssociationStatus
      | undefined

    if (
      receivedDomainAliasStatus === undefined ||
      typeof receivedDomainAliasStatus !== 'string'
    ) {
      throw new Error('Alias status was not set in response')
    }

    if (
      !Object.keys(types.DomainAssociationStatus).includes(
        receivedDomainAliasStatus,
      )
    ) {
      throw new Error(
        'Alias status was not a valid domain association status: ' +
          `${receivedDomainAliasStatus}`,
      )
    }
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : JSON.stringify(error)
    throw new Error(
      `Getting domain alias status body failed to parse: ${errorMessage}`,
    )
  }

  if (receivedDomainAliasStatus === undefined) {
    throw new Error('Response did not include a valid domain alias status')
  }

  return receivedDomainAliasStatus
}

export async function requestNewTXTRecord(
  firebaseApp: firebase.FirebaseApp,
  projectId: string,
): Promise<string> {
  const bearerToken = await auth.mintBearerToken(firebaseApp)
  const response = await makeRequestToServer({
    endpoint:
      getServerUri() + `f/v1/projects/${projectId}/newDNSVerificationRecord`,
    method: 'GET',
    bearerToken,
  })

  const body = await response?.text()
  // TODO(alex): Communicate errors to user
  if (response.status !== 201 || !body) {
    throw new Error(
      `Requesting DNS verification token failed with status ${response.status}`,
    )
  }

  let receivedTxtRecordToken
  try {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    receivedTxtRecordToken = JSON.parse(body)?.data?.txtRecord as
      | string
      | undefined
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : JSON.stringify(error)
    throw new Error(
      `DNS verification token request failed to parse: ${errorMessage}`,
    )
  }

  if (receivedTxtRecordToken === undefined) {
    throw new Error('Response did not include a valid TXT record token')
  }

  return receivedTxtRecordToken
}

function parseSourceViewIntoFirestoreStored(
  sourceId: string,
  sourceView: Partial<types.Source>,
): types.Result<types.FirestoreStored<types.Source>> {
  const sourceProject = sourceView.project
  if (!sourceProject) {
    throw new Error(
      'project field was not set on resulting new source object for ' +
        `project ${sourceId}`,
    )
  }
  const sourceType = sourceView.type
  if (!sourceType) {
    throw new Error(
      `type field was not set on resulting new source object ${sourceId}`,
    )
  }
  const sourceUrl = sourceView.source_url
  if (!sourceUrl) {
    throw new Error(
      'source_url field was not set on resulting new source ' +
        `object ${sourceId}`,
    )
  }
  const sourceContentSubfolder = sourceView.content_subfolder_path
  if (sourceContentSubfolder === undefined) {
    throw new Error(
      'content_subfolder_path field was not set on resulting new source ' +
        `object ${sourceId}`,
    )
  }

  const deployCredential = sourceView.deploy_credential

  const sourceValue: types.Source = {
    project: sourceProject,
    type: sourceType,
    source_url: sourceUrl,
    content_subfolder_path: sourceContentSubfolder,
  }

  if (deployCredential) {
    sourceValue.deploy_credential = deployCredential
  }

  // TODO(alex): Eliminate use of FirestoreStored.
  // TODO(alex): Pass out Partial<Source> or create a SourceView-like type for
  // client use instead of requiring a full Source locally simulated.
  return new types.FirestoreStored(sourceValue, `sources/${sourceId}`)
}

// TODO(alex): Migrate to backend API instead of using Firestore directly.
export async function createSourceAndSetAsProjectPrimary(
  firebaseApp: firebase.FirebaseApp,
  projectId: string,
  newSource: types.Source,
): Promise<types.FirestoreStored<types.Source>> {
  // TODO(alex): sanitize new source
  const sourceId = await createSource(firebaseApp, projectId, newSource)
  await setSourceAsPrimary(firebaseApp, projectId, sourceId)
  const sourceView = await getSource(firebaseApp, projectId, sourceId)

  const source = parseSourceViewIntoFirestoreStored(sourceId, sourceView)
  if (types.isError(source)) {
    const error = source
    throw new Error(
      `An error occurred when parsing new source ${error.message}`,
    )
  }

  return source
}

export async function createNewSource(
  firebaseApp: firebase.FirebaseApp,
  projectId: string,
  sourceToSave: types.Source,
): Promise<types.FirestoreStored<types.Source>> {
  let newSource
  try {
    newSource = await createSourceAndSetAsProjectPrimary(
      firebaseApp,
      projectId,
      sourceToSave,
    )
  } catch (error) {
    // TODO(alex): Communicate failure to user
    throw new Error(
      'Error occurred while creating new source: ' +
        `${error instanceof Error ? error.message : JSON.stringify(error)}`,
    )
  }

  if (newSource.value.deploy_credential) {
    throw new Error(
      'Newly stored source inexplicably has a pre-existing ' +
        `credential ID set: ${newSource.value.deploy_credential}`,
    )
  }

  if (newSource.value.type == types.SourceType.private_git_ssh) {
    let credentialId: string
    try {
      credentialId = await addDeployKeyToSource(
        firebaseApp,
        projectId,
        newSource.id,
      )
    } catch (error) {
      // TODO(alex): Communicate failure to user
      throw new Error(
        'Error occurred while creating new source: ' +
          `${error instanceof Error ? error.message : JSON.stringify(error)}`,
      )
    }

    const newSourceWithCredential = new types.FirestoreStored(
      { ...newSource.value, deploy_credential: credentialId },
      newSource.path,
    )
    return newSourceWithCredential
  }

  return newSource
}

// TODO(alex): Process response instead of returning it and forcing the caller
// to unpack it.
export async function requestProjectSync(
  firebaseApp: firebase.FirebaseApp,
  body: apiTypes.SynchronizationRequestBody,
): Promise<void> {
  const bearerToken = await auth.mintBearerToken(firebaseApp)
  const response = await makeRequestToServer({
    endpoint: getServerUri() + 'f/v1/sync',
    method: 'POST',
    bearerToken,
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    let errorMessage = 'Unknown error'
    try {
      const body = (await response.json()) as apiTypes.CommonResponseBody
      errorMessage = body.msg
    } catch (error) {
      const msg = types.isError(error) ? error.message : JSON.stringify(error)
      throw new Error(
        `Project synchronization response failed to parse: ${msg}`,
      )
    }
    throw new Error(`Project synchronization failed: ${errorMessage}`)
  }

  return
}

export async function registerPatron(
  firebaseApp: firebase.FirebaseApp,
  agreedToTermsOfService: boolean,
  agreedToPrivacyPolicy: boolean,
): Promise<void> {
  const bearerToken = await auth.mintBearerToken(firebaseApp)
  const response = await makeRequestToServer({
    endpoint: getServerUri() + 'f/v1/users',
    method: 'POST',
    bearerToken,
    body: JSON.stringify({
      status: {
        agreedToTermsOfService,
        agreedToPrivacyPolicy,
      },
    }),
  })

  if (!response.ok) {
    let errorMessage = 'Unknown error'
    try {
      const body = (await response.json()) as apiTypes.CommonResponseBody
      errorMessage = body.msg
    } catch (error) {
      const msg = types.isError(error) ? error.message : JSON.stringify(error)
      throw new Error(`Patron registration response failed to parse: ${msg}`)
    }
    throw new Error(`Patron registration failed: ${errorMessage}`)
  }

  return
}

export async function isPatronRegistered(
  firebaseApp: firebase.FirebaseApp,
): Promise<boolean> {
  const agreements = await getPatronAgreements(firebaseApp)

  return (
    (agreements.agreed_to_privacy_policy &&
      agreements.agreed_to_terms_of_service) ??
    false
  )
}

export async function getPatronAgreements(
  firebaseApp: firebase.FirebaseApp,
): Promise<types.PatronAgreements> {
  const bearerToken = await auth.mintBearerToken(firebaseApp)
  const response = await makeRequestToServer({
    endpoint: getServerUri() + `f/v1/users/current/agreements`,
    method: 'GET',
    bearerToken,
  })

  if (!response.ok) {
    let errorMessage = 'Unknown error'
    try {
      const body = (await response.json()) as apiTypes.CommonResponseBody
      errorMessage = body.msg
    } catch (error) {
      const msg = types.isError(error) ? error.message : JSON.stringify(error)
      throw new Error(`Patron agreements response failed to parse: ${msg}`)
    }
    throw new Error(`Patron agreements request failed: ${errorMessage}`)
  }

  try {
    const {
      data: { agreements },
    } = (await response.json()) as apiTypes.GetPatronAgreementsResponseBody

    return agreements
  } catch (error) {
    const msg = types.isError(error) ? error.message : JSON.stringify(error)
    throw new Error(`Patron agreements response failed to parse: ${msg}`)
  }
}

export async function createAccount(
  firebaseApp: firebase.FirebaseApp,
  accountName: string,
): Promise<string> {
  const bearerToken = await auth.mintBearerToken(firebaseApp)
  const response = await makeRequestToServer({
    endpoint: getServerUri() + 'f/v1/accounts',
    method: 'POST',
    bearerToken,
    body: JSON.stringify({
      accountName,
    }),
  })

  if (!response.ok) {
    let errorMessage = 'Unknown error'
    try {
      const body = (await response.json()) as apiTypes.CommonResponseBody
      errorMessage = body.msg
    } catch (error) {
      const msg = types.isError(error) ? error.message : JSON.stringify(error)
      throw new Error(`Create account response failed to parse: ${msg}`)
    }
    throw new Error(`Account creation failed: ${errorMessage}`)
  }

  try {
    const {
      data: { id },
    } = (await response.json()) as apiTypes.CreateAccountResponseBody

    return id
  } catch (error) {
    const msg = types.isError(error) ? error.message : JSON.stringify(error)
    throw new Error(`Account creation response failed to parse: ${msg}`)
  }
}

export async function createProject(
  firebaseApp: firebase.FirebaseApp,
): Promise<void> {
  const bearerToken = await auth.mintBearerToken(firebaseApp)
  const response = await makeRequestToServer({
    endpoint: getServerUri() + 'f/v1/projects',
    method: 'POST',
    bearerToken,
    body: JSON.stringify({}),
  })

  if (!response.ok) {
    let errorMessage = 'Unknown error'
    try {
      const body = (await response.json()) as apiTypes.CommonResponseBody
      errorMessage = body.msg
    } catch (error) {
      const msg = types.isError(error) ? error.message : JSON.stringify(error)
      throw new Error(`Create project response failed to parse: ${msg}`)
    }
    throw new Error(`Project creation failed: ${errorMessage}`)
  }

  return
}

export async function subscribe(
  firebaseApp: firebase.FirebaseApp,
  accountId: string,
  paymentMethodId: string,
): Promise<void> {
  const bearerToken = await auth.mintBearerToken(firebaseApp)
  const response = await makeRequestToServer({
    endpoint: getServerUri() + 'f/v1/subscriptions',
    method: 'POST',
    bearerToken,
    body: JSON.stringify({
      accountId,
      paymentMethodId,
    }),
  })

  if (!response.ok) {
    let errorMessage = 'Unknown error'
    try {
      const body = (await response.json()) as apiTypes.CommonResponseBody
      errorMessage = body.msg
    } catch (error) {
      const msg = types.isError(error) ? error.message : JSON.stringify(error)
      throw new Error(`Subscribe response failed to parse: ${msg}`)
    }
    throw new Error(`Subscription failed: ${errorMessage}`)
  }

  return
}

export async function getSourceSshDeployKey(
  firebaseApp: firebase.FirebaseApp,
  projectId: string,
  sourceId: string,
  credentialId: string,
): Promise<types.Option<string>> {
  const bearerToken = await auth.mintBearerToken(firebaseApp)
  const response = await makeRequestToServer({
    endpoint:
      getServerUri() +
      `f/v1/projects/${projectId}/sources/${sourceId}/ssh-keys/${credentialId}`,
    method: 'GET',
    bearerToken,
  })

  // TODO(alex): Communicate errors to user
  if (!response.ok) {
    throw new Error(`Get source SSH key failed with status ${response.status}`)
  }

  try {
    const responseBody = (await response.json()) as
      | apiTypes.GetSourceSshKeyResponseBody
      | undefined

    const publicKeyString =
      responseBody?.data?.value?.ssh_deploy_key?.public_key_string

    return publicKeyString ?? types.None
  } catch (error) {
    const errorMessage = types.isError(error)
      ? error.message
      : JSON.stringify(error)
    throw new Error(
      `Getting source SSH key response was not parseable: ${errorMessage}`,
    )
  }
}
